/**
 * Lightweight, dependency-free SEO manager.
 * Updates <title>, meta description, canonical, Open Graph, Twitter cards and
 * JSON-LD structured data on every route / language change.
 */

import { AGENCY, IMAGES } from '@/lib/site';
import { BUNDLES, DEFAULT_LANG } from '@/lib/i18n';

export const SITE_URL = 'https://www.romildamarketinghub.com';
const OG_IMAGE = `${SITE_URL}${IMAGES.ogImage}`;

export const seoFor = (lang, pathname) => {
  const bundle = BUNDLES[lang] || BUNDLES.en;
  return bundle.seo[pathname] || bundle.seo['*'];
};

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const upsertLink = (rel, href, extra = {}) => {
  const key = extra.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(key);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
};

/**
 * English is served on the bare URL; every other language is addressable via
 * ?lang= so it can be linked, shared and indexed on its own.
 */
const localisedUrl = (url, lang) =>
  lang === DEFAULT_LANG ? url : `${url}?lang=${lang}`;

/** Keeps exactly one og:locale:alternate tag per non-active language. */
const syncAlternateLocales = (activeLang) => {
  document.head
    .querySelectorAll('meta[property="og:locale:alternate"]')
    .forEach((el) => el.remove());
  Object.values(BUNDLES)
    .filter((b) => b.code !== activeLang)
    .forEach((b) => {
      const el = document.createElement('meta');
      el.setAttribute('property', 'og:locale:alternate');
      el.setAttribute('content', b.ogLocale);
      document.head.appendChild(el);
    });
};

const upsertJsonLd = (id, data) => {  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const organizationLd = (bundle) => ({
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#organization`,
  name: AGENCY.name,
  alternateName: 'Romilda Marketing',
  url: SITE_URL,
  logo: `${SITE_URL}${IMAGES.logoSvg}`,
  image: OG_IMAGE,
  email: AGENCY.email,
  telephone: AGENCY.whatsappNumber,
  description: bundle.ldDescription,
  priceRange: '$$$',
  areaServed: 'Worldwide',
  knowsAbout: bundle.marquee,
  founder: {
    '@type': 'Person',
    name: 'Romilda Bahollari',
    jobTitle: 'Founder & Marketing Strategist',
    sameAs: [AGENCY.linkedin, AGENCY.instagram].filter(Boolean),
  },
  sameAs: [AGENCY.instagram, AGENCY.tiktok, AGENCY.facebook, AGENCY.linkedin].filter(
    Boolean,
  ),
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: AGENCY.email,
      telephone: AGENCY.whatsappNumber,
      availableLanguage: ['English', 'German', 'Albanian'],
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: bundle.footer.servicesHeading,
    itemListElement: bundle.services.map((s) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: s.title,
        description: s.short,
        url: `${SITE_URL}/services#${s.slug}`,
        provider: { '@id': `${SITE_URL}/#organization` },
      },
    })),
  },
});

const websiteLd = (bundle) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: AGENCY.name,
  publisher: { '@id': `${SITE_URL}/#organization` },
  inLanguage: bundle.htmlLang,
});

const breadcrumbLd = (bundle, pathname) => {
  const labels = {
    '/services': bundle.nav.services,
    '/about': bundle.nav.about,
    '/contact': bundle.nav.contact,
  };
  const items = [
    { '@type': 'ListItem', position: 1, name: bundle.nav.home, item: SITE_URL },
  ];
  if (labels[pathname]) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: labels[pathname],
      item: `${SITE_URL}${pathname}`,
    });
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
};

/** Applies every meta tag for the given language + route. */
export const applySeo = (lang = 'en', pathname = '/') => {
  const bundle = BUNDLES[lang] || BUNDLES.en;
  const seo = seoFor(lang, pathname);
  if (!seo) return;

  const url = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;

  document.title = seo.title;  upsertMeta('meta[name="description"]', {
    name: 'description',
    content: seo.description,
  });
  if (seo.keywords) {
    upsertMeta('meta[name="keywords"]', { name: 'keywords', content: seo.keywords });
  }
  upsertMeta('meta[name="robots"]', {
    name: 'robots',
    content: seo.noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1',
  });
  upsertLink('canonical', localisedUrl(url, lang));

  // Open Graph
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
  upsertMeta('meta[property="og:site_name"]', {
    property: 'og:site_name',
    content: AGENCY.name,
  });
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title });
  upsertMeta('meta[property="og:description"]', {
    property: 'og:description',
    content: seo.description,
  });
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
  upsertMeta('meta[property="og:image"]', { property: 'og:image', content: OG_IMAGE });
  upsertMeta('meta[property="og:image:width"]', {
    property: 'og:image:width',
    content: '1200',
  });
  upsertMeta('meta[property="og:image:height"]', {
    property: 'og:image:height',
    content: '630',
  });
  upsertMeta('meta[property="og:locale"]', {
    property: 'og:locale',
    content: bundle.ogLocale,
  });
  syncAlternateLocales(lang);

  // hreflang alternates. The site keeps one URL per route and selects the
  // language via ?lang=, so the canonical of a translated view must carry the
  // parameter too — otherwise Google folds the variants together and the
  // alternates are ignored.
  Object.values(BUNDLES).forEach((b) => {
    upsertLink('alternate', localisedUrl(url, b.code), { hreflang: b.htmlLang });
  });
  upsertLink('alternate', url, { hreflang: 'x-default' });

  // Twitter
  upsertMeta('meta[name="twitter:card"]', {
    name: 'twitter:card',
    content: 'summary_large_image',
  });
  upsertMeta('meta[name="twitter:title"]', {
    name: 'twitter:title',
    content: seo.title,
  });
  upsertMeta('meta[name="twitter:description"]', {
    name: 'twitter:description',
    content: seo.description,
  });
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: OG_IMAGE });

  // Structured data
  upsertJsonLd('ld-organization', organizationLd(bundle));
  upsertJsonLd('ld-website', websiteLd(bundle));
  upsertJsonLd('ld-breadcrumb', breadcrumbLd(bundle, pathname));
};

export default applySeo;
